import { getCachedSubForSid } from './oidcManager';
import { lddGlobalSessionInfo } from '../navigationTypes';
import { APP_CODE } from '../appConstants';
import { TrustShieldExportRecord, ItemType, ExportStatus, RealtiCheckStatus } from './trustShieldTypes';

const TS_BASE_PATH = '/ts/';

/**
 * True when the current firm is provisioned for RealtiTrust (TrustShield).
 * Beta firms are switched on via the `TrustShieldExport` firm flag, so this
 * doubles as the beta gate — RealtiTrust-specific UI (buttons, alerts, the
 * file-number-change warning) only appears for these firms.
 */
export function isTrustShieldEnabled(session: lddGlobalSessionInfo | null | undefined): boolean {
    return session?.appCode === APP_CODE.BRITISH_COLUMBIA
        && session?.firm?.TrustRegistered === '1'
        && session?.firm?.TrustShieldExport === '1';
}

/**
 * A record whose bank/check state (or, for deposits, whose edit chain)
 * terminates in a void is no longer in trust. Such a record does not count
 * toward a recorded total, and does not block re-export of the same hash or
 * content from Realti.
 *
 * Deposits: chain-aware. The server-resolved `effective` block flags
 * `is_truly_voided` when the chain terminates in a void - either a direct
 * void on the original, or an edited-then-voided successor. The puracct
 * iframe mirrors this by treating such rows as "fresh".
 *
 * Payments: direct void detection on the record's own bank/check state.
 * Load-bearing assumption: payments do NOT have an edit chain (TrustShield's
 * edit flow exists only for receipts), so it is safe to read raw bankStatus
 * / checkStatus / isVoided without consulting `effective`. If a payment edit
 * chain is ever introduced server-side, this branch must be re-derived from
 * `effective.is_truly_voided` to avoid marking active-edit-chain originals
 * as fresh and minting duplicate exports.
 *
 * Reversals: never themselves fresh - a `reversal` row IS the void of
 * another transaction (its own bank-tx is typically PENDING/CLEARED, not
 * VOID), so the default bank-status branch would misclassify it.
 */
export function isVoidedExportRecord(r: TrustShieldExportRecord): boolean {
    if (r.type === ItemType.DEPOSIT) return r.effective?.is_truly_voided === true;
    if (r.kind === 'reversal') return false;
    // Cheques carry the void in two places and the payload may populate
    // either. Reading only checkStatus decides a money figure from one field
    // when two are available, so both are honoured. Keep this even once
    // checkStatus is reliably populated: the cost of the miss is a voided
    // cheque counted as money spent, not a wrong badge.
    if (r.kind === 'disbursement-check') return r.checkStatus === 'VOID' || r.bankStatus === 'VOID';
    if (r.kind === 'm2m-transfer-immediate') return r.isVoided === true;
    return r.bankStatus === 'VOID';
}

/**
 * The records whose amounts the trust account actually holds, out of a set
 * that all describe the same Realti item.
 *
 * A void stays in the caller's list so the user can see what became of the
 * money, but it is no longer in trust. A DRAFT has not moved money at all.
 *
 * Nor has a REJECTED or CANCELLED request: an approver denied it or the user
 * withdrew it, and no cheque was ever written. Counting either would report an
 * item as satisfied by money that never left, and offer a shortfall smaller
 * than the one still outstanding. hasActivePostedTransactions below and the
 * webapp's row rule both already exclude them; this is the third reader, and
 * it is the one the totals are taken from.
 *
 * Takes the whole set rather than one record because "still live" can depend
 * on siblings: a correction supersedes the record it replaced.
 */
export function liveRecords(
    records: readonly TrustShieldExportRecord[],
): TrustShieldExportRecord[] {
    return records.filter(
        (r) => r.status !== ExportStatus.DRAFT
            && r.status !== ExportStatus.REJECTED
            && r.status !== ExportStatus.CANCELLED
            && !isSupersededExportRecord(r)
            && !isVoidedExportRecord(r),
    );
}

/**
 * True when a correction has replaced this record.
 *
 * `effective` resolves to the terminal node of the record's edit chain, so a
 * superseded row reports its successor's transaction_id and only the live row
 * reports its own. This is the ONLY field that separates them: an original and
 * its replacement both carry is_edited true and is_truly_voided false, so
 * neither flag can be used, and counting rows under one reference cannot
 * either - two rows are ambiguous between a split and a correction.
 */
export function isSupersededExportRecord(r: TrustShieldExportRecord): boolean {
    if (r.effective == null || r.transaction_id == null) return false;
    return r.effective.transaction_id !== r.transaction_id;
}

/**
 * What a Realti item has actually had recorded against it. The single
 * definition of that total across the app - the send list, the verification
 * screen and the receipt-details modal all read it from here.
 */
export function recordedTotalCents(records: readonly TrustShieldExportRecord[]): number {
    return liveRecords(records).reduce((sum, r) => sum + r.amount_cents, 0);
}

export interface CounterpartySeed {
    /** Who the further cheque or receipt is paid to, or received from. */
    name: string;
    /** Whether that name is the file's client. */
    isClient: boolean;
}

/**
 * What to seed the payee, or payor, of a further cheque or receipt against this
 * item. Taken from the records that still hold the money, so a voided or
 * corrected leg cannot name the counterparty.
 *
 * The name and the "is the client" setting travel together, and neither is
 * guessed. The client's name used to stand in when nothing was recorded, which
 * left the field naming the client while the box read unticked - nobody had
 * chosen the client (21821). Seeding nothing keeps the two in agreement.
 */
export function counterpartySeed(
    records: readonly TrustShieldExportRecord[],
): CounterpartySeed {
    const seed = liveRecords(records)[0];
    return {
        name: seed?.payee_payor || '',
        isClient: seed?.is_payee_client ?? false,
    };
}

/**
 * The amount to hand the recording surface when the user records another
 * cheque or receipt against a Realti item.
 *
 * The shortfall, and zero rather than negative once the item is recorded in
 * full: a negative amount is not a recordable transaction, and handing back
 * the full amount would invite double recording. What each recording surface
 * makes of a zero is its own business - see the hand-off builders.
 */
export function splitPrefillCents(
    realtiAmountCents: number,
    records: readonly TrustShieldExportRecord[],
): number {
    return Math.max(0, realtiAmountCents - recordedTotalCents(records));
}

/**
 * True when the matter has at least one transaction still live on the trust
 * ledger — posted (not a draft, and not a rejected request that never moved
 * money) and not voided. Used to block a file-number change until the user
 * voids everything.
 */
export function hasActivePostedTransactions(records: TrustShieldExportRecord[]): boolean {
    return records.some(
        (r) =>
            r.status !== ExportStatus.DRAFT &&
            r.status !== ExportStatus.REJECTED &&
            !isVoidedExportRecord(r),
    );
}

interface TrustShieldUrlParams {
    [key: string]: string | undefined;
}

/**
 * Build a TrustShield client URL with the given parameters.
 *
 * Second arg (always pass a `sid` when building iframe/tab URLs):
 *   - string               → SSO flow (adds `sso` + `SessionID`). Use when
 *                            opening a fresh tab where the user may need to
 *                            auto-login via the UV session.
 *   - { sid, sso: false }  → no SSO — for already-authenticated embedded iframes.
 *
 * If `sid` has already been resolved to a `sub` this tab (via
 * `initTrustShieldSSO`), appends `account=<sub>` so the TS SPA lands directly
 * in the right per-user partition.
 */
export function buildTrustShieldUrl(
    params: TrustShieldUrlParams,
    sidOrOptions?: string | { sid?: string; sso?: boolean }
): string {
    const opts: { sid?: string; sso?: boolean } = typeof sidOrOptions === 'string'
        ? { sid: sidOrOptions, sso: true }
        : (sidOrOptions ?? {});
    const { sid, sso } = opts;

    const searchParams = new URLSearchParams();
    if (sso) searchParams.set('sso', '');
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, value);
    }
    if (sid) {
        if (sso) searchParams.set('SessionID', sid);
        const sub = getCachedSubForSid(sid);
        if (sub) searchParams.set('account', sub);
    }
    return `${TS_BASE_PATH}?${searchParams.toString()}`;
}

export const STATUS_BADGE_VARIANT: Record<string, string> = {
    DRAFT: 'secondary',
    PENDING: 'warning',
    POSTED: 'success',
    CLEARED: 'success',
    RECONCILED: 'success',
    APPROVED: 'info',
    REJECTED: 'danger',
    CANCELLED: 'dark',
};

export function getStatusDisplayLabel(status: string): string {
    // Title-case the raw TrustShield status (CLEARED → Cleared, DRAFT → Draft)
    return status.charAt(0) + status.slice(1).toLowerCase();
}

// Bank transaction status labels that differ from the title-cased status.
// PENDING means the bank has not confirmed it yet, which Realti calls "Posted".
const BANK_STATUS_LABEL: Record<string, string> = {
    PENDING: 'Posted',
};

export function getBankStatusLabel(status: string): string {
    return BANK_STATUS_LABEL[status] ?? getStatusDisplayLabel(status);
}

// The colour for each bank-status label above. STATUS_BADGE_VARIANT is keyed by
// request status, where PENDING is a request awaiting approval and so amber.
// On a receipt PENDING reads "Posted", a settled state, so it is green.
const BANK_STATUS_VARIANT: Record<string, string> = {
    PENDING: 'success',
    CLEARED: 'success',
    RECONCILED: 'success',
    VOID: 'secondary',
};

// A cheque's status in the words and colours the webapp Funds Out row uses
// (TrustShieldFuncs.js deriveBadgeFromMatrix), so a single cheque reads the
// same in both places. An unprinted cheque has no cheque word there and falls
// back to the request's "Posted", so it does here too. Label and colour share
// one row so they cannot disagree.
const CHEQUE_STATUS_BADGE: Record<RealtiCheckStatus, { label: string; variant: string }> = {
    PENDING: { label: 'Posted', variant: 'success' },
    PRINTED: { label: 'Printed', variant: 'success' },
    CLEARED: { label: 'Cleared', variant: 'success' },
    STALE: { label: 'Stale', variant: 'warning' },
    STOP_PAYMENT: { label: 'Stop Payment', variant: 'warning' },
    VOID: { label: 'Void', variant: 'secondary' },
};

/**
 * The cheque status badge, or null when the record is not a cheque or its
 * cheque status has not arrived. An EFT or wire has no cheque status.
 */
export function chequeStatusBadge(r: TrustShieldExportRecord): { label: string; variant: string } | null {
    if (r.kind !== 'disbursement-check' || r.checkStatus == null) return null;
    return CHEQUE_STATUS_BADGE[r.checkStatus];
}

/**
 * The status label for an export record. A receipt's status is its bank
 * transaction status, and so is a wire's, EFT's or bank draft's. A cheque's
 * status is its cheque status. Any other payment or transfer shows its request
 * status, where PENDING means awaiting approval, so it keeps the plain label.
 */
export function getExportRecordStatusLabel(r: TrustShieldExportRecord): string {
    const bank = bankStatusShown(r);
    if (bank != null) return getBankStatusLabel(bank);
    return chequeStatusBadge(r)?.label ?? getStatusDisplayLabel(r.status);
}

/** The badge colour that goes with getExportRecordStatusLabel. */
export function getExportRecordStatusVariant(r: TrustShieldExportRecord): string {
    const bank = bankStatusShown(r);
    const own = bank != null ? BANK_STATUS_VARIANT[bank] : chequeStatusBadge(r)?.variant;
    return own ?? STATUS_BADGE_VARIANT[r.status] ?? 'secondary';
}

/**
 * The bank status a record shows in place of its request status, or null.
 * A receipt's status is its bank status. A wire, EFT or bank draft has no
 * cheque status, and its request status stays POSTED for good, so it shows
 * its bank status once one has arrived (22022).
 */
function bankStatusShown(r: TrustShieldExportRecord): string | null {
    if (r.type === ItemType.DEPOSIT) return r.status;
    if (r.kind === 'disbursement-non-check') return r.bankStatus ?? null;
    return null;
}

const LDD_IFRAME_SELECTOR = '#lddIFrame > iframe';

/**
 * Notify the legacy ASP iframe that a TrustShield export completed,
 * so it can refresh export status indicators.
 */
export function notifyLegacyIframeExportComplete(): void {
    const iframe = document.querySelector(LDD_IFRAME_SELECTOR) as HTMLIFrameElement | null;
    iframe?.contentWindow?.postMessage(
        { type: 'trustShieldExportComplete' },
        window.location.origin
    );
}

interface MirrorArgs {
    hash: string;
    amountCents: number;
    description: string;
}

type MirrorFnName = 'mirrorReceiptToRealti' | 'mirrorPaymentToRealti';

function callRealtiMirror(fnName: MirrorFnName, args: MirrorArgs): boolean {
    const iframe = document.querySelector(LDD_IFRAME_SELECTOR) as HTMLIFrameElement | null;
    const tsExport = (iframe?.contentWindow as { TrustShieldExport?: Record<MirrorFnName, ((a: MirrorArgs) => unknown) | undefined> } | null)?.TrustShieldExport;
    const fn = tsExport?.[fnName];
    if (typeof fn !== 'function') {
        console.warn(`[TrustShield] ${fnName} not available on Realti iframe`);
        return false;
    }
    try {
        return fn(args) != null;
    } catch (err) {
        console.warn(`[TrustShield] ${fnName} threw:`, err);
        return false;
    }
}

/**
 * Mirror a freshly saved TrustShield deposit back into Realti so the receipt
 * also appears on the Realti file (Funds In + Statement of Adjustments).
 *
 * Reuse the original `items[0].hash` from the openNewDeposit payload — Realti
 * uses it to keep the row marked as exported. Returns false on failure
 * (function missing, validation null, or throw); the caller should surface a
 * non-fatal warning rather than unwinding the TrustShield save.
 */
export function mirrorReceiptToRealti(args: MirrorArgs): boolean {
    return callRealtiMirror('mirrorReceiptToRealti', args);
}

/**
 * Symmetric to mirrorReceiptToRealti for the new-payment flow. Reuse the
 * original `items[0].hash` from the openNewPayment payload so Realti keeps
 * the row linked to the TrustShield record via IssueAdjust/@TrustShieldHash.
 */
export function mirrorPaymentToRealti(args: MirrorArgs): boolean {
    return callRealtiMirror('mirrorPaymentToRealti', args);
}

export function formatCents(cents: number): string {
    return (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

/**
 * Format a date string for display. Handles both ISO ("2026-03-20") and
 * numeric DATEONLY ("20260320") formats from the TrustShield API.
 */
export function formatDate(dateStr: string): string {
    if (!dateStr) return '';

    let normalized = dateStr;
    // MSSQL DATEONLY can serialize as "20260320" — insert dashes
    if (/^\d{8}$/.test(dateStr)) {
        normalized = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }

    const date = new Date(normalized + 'T00:00:00');
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}
